import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Expand,
  ExternalLink,
  FileCheck2,
  Layers3,
  MessageCircle,
  Palette,
  Phone,
  RefreshCcw,
  Ruler,
  ShieldCheck,
  Star,
  Truck,
  Wrench,
  X,
} from "lucide-react";
import FloatingContact from "../components/FloatingContact";
import Footer from "../components/Footer";
import ScrollControls from "../components/ScrollControls";
import ProductCard from "../components/ProductCard";
import logo from "../img/logo.png";
import { API_BASE } from "../config";
import { trackAnalyticsEvent, trackContactClick, trackProductSelect, trackProductView } from "../utils/analytics";
import { getDisplayColor, getDisplayPrice } from "../utils/productDisplay";
import { formatProductDetailLabel } from "../utils/productDetails";
import { getOptimizedImageUrl, getResponsiveImageSrcSet, normalizeImageUrl } from "../utils/imageUrl";
import { localizeProduct } from "../utils/productLocalization";
import { setProductSeo } from "../utils/seoUtils";

const HIDDEN_DETAIL_KEYS = new Set([
  "nguồn ảnh",
  "source",
  "image",
  "ảnh",
  "màu sắc",
  "realimages",
  "customerimages",
  "reviews",
]);

function getProductId(product) {
  return String(product?._id || product?.slug || product?.name || "");
}

function getProductTimestamp(product) {
  return Date.parse(product?.createdAt || product?.updatedAt || "") || 0;
}

function getDetailEntries(details) {
  if (!details || typeof details !== "object") return [];

  return Object.entries(details).filter(([key, value]) => {
    const normalizedKey = String(key).trim().toLowerCase();
    return value !== null &&
      value !== undefined &&
      String(value).trim() &&
      !HIDDEN_DETAIL_KEYS.has(normalizedKey);
  });
}

function getVerifiedImages(product) {
  const sources = [
    ...(Array.isArray(product?.realImages) ? product.realImages : []),
    ...(Array.isArray(product?.customerImages) ? product.customerImages : []),
    ...(Array.isArray(product?.details?.realImages) ? product.details.realImages : []),
    ...(Array.isArray(product?.details?.customerImages) ? product.details.customerImages : []),
  ];

  return [...new Set(sources.map(normalizeImageUrl).filter(Boolean))];
}

function ProductDetailSkeleton() {
  return (
    <div className="min-h-screen">
      <div className="h-[68px] border-b border-gray-100 bg-white" />
      <div className="mx-auto max-w-7xl animate-pulse px-4 pb-16 pt-10 sm:px-6 lg:px-8">
        <div className="mb-8 h-4 w-64 rounded bg-gray-200" />
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(360px,0.75fr)]">
          <div className="aspect-[4/3] rounded-lg bg-gray-200" />
          <div className="space-y-5">
            <div className="h-10 w-4/5 rounded bg-gray-200" />
            <div className="h-8 w-40 rounded bg-gray-200" />
            <div className="h-24 rounded bg-gray-200" />
            <div className="h-12 rounded bg-gray-200" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setLoading(true);
    setCurrentIndex(0);
    window.scrollTo({ top: 0, behavior: "auto" });

    fetch(`${API_BASE}/products/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Product fetch failed: ${res.status}`);
        return res.json();
      })
      .then((data) => setProduct(localizeProduct(data) || null))
      .catch((err) => {
        console.error("Error:", err);
        setProduct(null);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (product) setProductSeo(product, slug);
  }, [product, slug]);

  useEffect(() => {
    if (!product) return;
    trackProductView(product, {
      content_ids: [getProductId(product)],
      content_name: product.name,
      content_type: "product",
      content_category: product.type,
    });
  }, [product]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const images = [product.image, ...(Array.isArray(product.gallery) ? product.gallery : [])].filter(Boolean);
    return [...new Set(images)];
  }, [product]);

  const detailEntries = useMemo(() => getDetailEntries(product?.details), [product]);
  const verifiedImages = useMemo(() => getVerifiedImages(product), [product]);
  const mainImage = gallery[currentIndex] || product?.image || "";

  useEffect(() => {
    if (!lightboxOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setLightboxOpen(false);
      if (event.key === "ArrowLeft") {
        setCurrentIndex((index) => (index === 0 ? gallery.length - 1 : index - 1));
      }
      if (event.key === "ArrowRight") {
        setCurrentIndex((index) => (index === gallery.length - 1 ? 0 : index + 1));
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [gallery.length, lightboxOpen]);

  if (loading) return <ProductDetailSkeleton />;

  if (!product) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
        <h1 className="text-3xl font-bold">Không tìm thấy sản phẩm</h1>
        <p className="mt-3 text-gray-600">Sản phẩm có thể đã được thay đổi hoặc tạm ẩn.</p>
        <Link to="/#products" className="mt-6 rounded-md bg-[var(--color-primary)] px-5 py-3 font-semibold text-white">
          Xem danh sách sản phẩm
        </Link>
      </div>
    );
  }

  const contactPayload = {
    content_ids: [getProductId(product)],
    content_name: product.name,
    content_type: "product",
    item_id: getProductId(product),
    item_name: product.name,
    item_category: product.type,
  };

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex min-h-11 items-center gap-2 !bg-transparent !p-0 !shadow-none"
            aria-label="Về trang chủ"
          >
            <img src={logo} alt="Cưng Home" className="h-10 w-28 object-contain sm:w-32" />
            <span className="hidden font-['Playfair_Display'] text-lg font-bold text-[var(--color-primary)] sm:inline">
              Xưởng Sofa
            </span>
          </button>

          <div className="flex items-center gap-2">
            <Link
              to={`/?type=${encodeURIComponent(product.type || "Sofa")}#products`}
              className="hidden items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] sm:flex"
            >
              <ArrowLeft className="h-4 w-4" />
              Xem sản phẩm
            </Link>
            <a
              href="tel:0984842841"
              onClick={() => trackContactClick("Phone product detail", contactPayload)}
              className="flex min-h-11 items-center gap-2 rounded-md bg-[var(--color-primary)] px-3 text-sm font-semibold !text-white transition hover:bg-[#805b42]"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">0984 842 841</span>
              <span className="sm:hidden">Gọi ngay</span>
            </a>
          </div>
        </div>
      </header>

      <main>
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-5 sm:px-6 lg:px-8 lg:pt-7">
          <nav className="scrollbar-hide mb-5 flex items-center gap-2 overflow-x-auto whitespace-nowrap text-sm text-gray-500">
            <Link
              to="/"
              className="flex min-h-11 items-center font-medium text-[var(--color-primary)] hover:underline md:min-h-0"
            >
              Trang chủ
            </Link>
            <span>/</span>
            <Link
              to={`/?type=${encodeURIComponent(product.type || "Sofa")}#products`}
              className="flex min-h-11 items-center font-medium text-[var(--color-primary)] hover:underline md:min-h-0"
            >
              {product.type || "Sản phẩm"}
            </Link>
            <span>/</span>
            <span className="truncate font-medium text-gray-700">{product.name}</span>
          </nav>

          <section className="grid items-start gap-8 lg:grid-cols-[minmax(0,1.18fr)_minmax(360px,0.82fr)] lg:gap-12">
            <div className="min-w-0">
              <div className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-[#f1efeb]">
                <img
                  src={getOptimizedImageUrl(mainImage, { width: 1200, quality: "auto:good" })}
                  srcSet={getResponsiveImageSrcSet(
                    mainImage,
                    [480, 800, 1200, 1600],
                    { quality: "auto:good" }
                  )}
                  sizes="(max-width: 1023px) calc(100vw - 32px), 58vw"
                  alt={`${product.name} - ảnh ${currentIndex + 1}`}
                  fetchPriority="high"
                  decoding="async"
                  onClick={() => setLightboxOpen(true)}
                  className="h-full w-full cursor-zoom-in object-cover"
                />
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  aria-label="Phóng to ảnh"
                  className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full !bg-white/90 !p-0 !text-gray-700 shadow-sm backdrop-blur transition hover:!bg-white"
                >
                  <Expand className="h-5 w-5" />
                </button>
                {gallery.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setCurrentIndex((index) => (index === 0 ? gallery.length - 1 : index - 1))}
                      aria-label="Ảnh trước"
                      className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full !bg-white/90 !p-0 !text-gray-700 opacity-100 shadow-sm transition hover:!bg-white lg:opacity-0 lg:group-hover:opacity-100"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentIndex((index) => (index === gallery.length - 1 ? 0 : index + 1))}
                      aria-label="Ảnh sau"
                      className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full !bg-white/90 !p-0 !text-gray-700 opacity-100 shadow-sm transition hover:!bg-white lg:opacity-0 lg:group-hover:opacity-100"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    <div className="absolute bottom-3 right-3 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                      {currentIndex + 1} / {gallery.length}
                    </div>
                  </>
                )}
              </div>

              {gallery.length > 1 && (
                <div className="scrollbar-hide mt-3 flex gap-2 overflow-x-auto pb-1">
                  {gallery.map((image, index) => (
                    <button
                      key={image}
                      type="button"
                      onClick={() => {
                        setCurrentIndex(index);
                        trackAnalyticsEvent("view_product_image", {
                          item_id: getProductId(product),
                          item_name: product.name,
                          image_index: index + 1,
                        });
                      }}
                      aria-label={`Xem ảnh ${index + 1}`}
                      className={`h-20 w-20 shrink-0 overflow-hidden rounded-md !p-0 !shadow-none sm:h-24 sm:w-24 ${
                        index === currentIndex
                          ? "ring-2 ring-[var(--color-primary)] ring-offset-2"
                          : "opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={getOptimizedImageUrl(image, { width: 192 })}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:sticky lg:top-24">
              <p className="mb-2 text-sm font-bold uppercase text-[var(--color-primary)]">
                {product.type || "Sản phẩm Cưng Home"}
              </p>
              <h1 className="text-3xl font-bold leading-tight sm:text-4xl">{product.name}</h1>
              <div className="mt-4 flex items-end justify-between gap-3 border-b border-gray-200 pb-5">
                <div>
                  <p className="text-2xl font-bold text-[var(--color-primary)]">
                    {getDisplayPrice(product.price)}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Báo giá theo kích thước và chất liệu lựa chọn.
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
                  Nhận may đo
                </span>
              </div>

              <RealProductImages product={product} images={verifiedImages} />

              <div className="grid gap-3 py-5 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                <InfoItem icon={Ruler} label="Kích thước" value={product.size || "Tuỳ chỉnh theo yêu cầu"} />
                <InfoItem icon={Palette} label="Màu sắc" value={getDisplayColor()} />
                {product.material && (
                  <InfoItem icon={Layers3} label="Chất liệu" value={product.material} wide />
                )}
              </div>

              <div className="space-y-2">
                <a
                  href="https://m.me/cunghome21"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackContactClick("Messenger product detail", contactPayload)}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-[var(--color-primary)] px-5 py-3 font-semibold !text-white shadow-sm transition hover:bg-[#805b42]"
                >
                  <MessageCircle className="h-5 w-5" />
                  Tư vấn qua fanpage
                </a>
                <a
                  href="https://zalo.me/0984842841"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackContactClick("Zalo product detail", contactPayload)}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-md border border-[var(--color-primary)] bg-white px-5 py-3 font-semibold text-[var(--color-primary)] transition hover:bg-[#f7f0ea]"
                >
                  Gửi ảnh không gian qua Zalo
                </a>
              </div>

              <ul className="mt-5 space-y-2 border-t border-gray-200 pt-5 text-sm text-gray-600">
                {[
                  "Tư vấn kích thước theo diện tích phòng",
                  "Tuỳ chọn màu và chất liệu bọc",
                  "Xác nhận cấu hình trước khi sản xuất",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-primary)]" />
                    {item}
                  </li>
                ))}
              </ul>
            </aside>
          </section>

          <ServiceHighlights warranty={product.details?.["bảo hành"]} />

          <section className="grid gap-10 border-t border-gray-200 py-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
            <div>
              <p className="text-sm font-bold uppercase text-[var(--color-primary)]">Thiết kế</p>
              <h2 className="mt-2 text-2xl font-bold sm:text-3xl">Mô tả sản phẩm</h2>
              <p className="mt-4 whitespace-pre-line leading-7 text-gray-700">
                {product.description}
              </p>
            </div>

            <div>
              <p className="text-sm font-bold uppercase text-[var(--color-primary)]">Chi tiết</p>
              <h2 className="mt-2 text-2xl font-bold sm:text-3xl">Thông tin sản phẩm</h2>
              {detailEntries.length > 0 ? (
                <dl className="mt-5 divide-y divide-gray-200 border-y border-gray-200">
                  {detailEntries.map(([key, value]) => (
                    <div key={key} className="grid gap-1 py-3 sm:grid-cols-[150px_1fr] sm:gap-5">
                      <dt className="text-sm font-semibold text-gray-500">{formatProductDetailLabel(key)}</dt>
                      <dd className="text-sm font-medium leading-6 text-gray-800">{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="mt-4 text-gray-600">
                  Liên hệ Cưng Home để nhận thông tin cấu hình phù hợp với nhu cầu sử dụng.
                </p>
              )}

              {product.details?.image && (
                <div className="mt-5 overflow-hidden rounded-lg">
                  <img
                    src={getOptimizedImageUrl(product.details.image, { width: 900 })}
                    alt={`Thông số ${product.name}`}
                    loading="lazy"
                    decoding="async"
                    className="max-h-80 w-full object-contain"
                  />
                </div>
              )}
            </div>
          </section>

          <section className="border-t border-gray-200 py-12">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase text-[var(--color-primary)]">Đặt sofa theo nhu cầu</p>
              <h2 className="mt-2 text-2xl font-bold sm:text-3xl">Quy trình tư vấn và sản xuất</h2>
            </div>
            <div className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["01", "Gửi nhu cầu", "Chia sẻ kích thước phòng, mẫu yêu thích và nhu cầu sử dụng."],
                ["02", "Chọn cấu hình", "Thống nhất kích thước, màu sắc, chất liệu và chi tiết sản phẩm."],
                ["03", "Xác nhận sản xuất", "Cưng Home xác nhận báo giá và cấu hình trước khi thực hiện."],
                ["04", "Giao và lắp đặt", "Hẹn lịch giao nhận phù hợp sau khi sản phẩm hoàn thiện."],
              ].map(([number, title, description]) => (
                <div key={number} className="border-l-2 border-[var(--color-primary)] pl-4">
                  <span className="text-sm font-bold text-[var(--color-primary)]">{number}</span>
                  <h3 className="mt-2 text-lg font-bold">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{description}</p>
                </div>
              ))}
            </div>
          </section>

          <CustomerReviews product={product} />
          <PolicySection />
          <RelatedProducts currentProduct={product} />
        </div>
      </main>

      <Footer />
      <ScrollControls />
      <FloatingContact />

      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxOpen(false)}
          >
            <div className="relative flex h-full w-full items-center justify-center p-4 sm:p-10">
              <img
                src={mainImage}
                alt={`${product.name} - ảnh phóng to`}
                className="max-h-full max-w-full object-contain"
                onClick={(event) => event.stopPropagation()}
              />
              {gallery.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setCurrentIndex((index) => (index === 0 ? gallery.length - 1 : index - 1));
                    }}
                    aria-label="Ảnh trước"
                    className="absolute left-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full !bg-white/15 !p-0 !text-white backdrop-blur hover:!bg-white/25 sm:left-6"
                  >
                    <ChevronLeft className="h-7 w-7" />
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setCurrentIndex((index) => (index === gallery.length - 1 ? 0 : index + 1));
                    }}
                    aria-label="Ảnh sau"
                    className="absolute right-3 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full !bg-white/15 !p-0 !text-white backdrop-blur hover:!bg-white/25 sm:right-6"
                  >
                    <ChevronRight className="h-7 w-7" />
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setLightboxOpen(false)}
                aria-label="Đóng ảnh"
                className="absolute right-3 top-3 flex h-12 w-12 items-center justify-center rounded-full !bg-white/15 !p-0 !text-white backdrop-blur hover:!bg-white/25 sm:right-6 sm:top-6"
              >
                <X className="h-7 w-7" />
              </button>
              <div className="absolute bottom-5 rounded-full bg-black/60 px-3 py-1 text-sm font-semibold text-white">
                {currentIndex + 1} / {gallery.length}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function InfoItem({ icon: Icon, label, value, wide = false }) {
  return (
    <div className={`flex items-start gap-3 rounded-md border border-gray-200 bg-white p-3 ${wide ? "sm:col-span-2 lg:col-span-1 xl:col-span-2" : ""}`}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-primary)]" />
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase text-gray-500">{label}</p>
        <p className="mt-1 text-sm font-medium leading-5 text-gray-800">{value}</p>
      </div>
    </div>
  );
}

function ServiceHighlights({ warranty }) {
  const items = [
    { icon: Truck, title: "Giao nhận", description: "Hẹn lịch theo khu vực" },
    { icon: Wrench, title: "Lắp đặt", description: "Hỗ trợ khi giao hàng" },
    { icon: ShieldCheck, title: "Bảo hành", description: warranty || "3 năm (36 tháng)" },
    { icon: Palette, title: "May đo", description: "Màu và chất liệu tuỳ chọn" },
  ];

  return (
    <section className="my-12 grid grid-cols-2 border-y border-gray-200 sm:grid-cols-4">
      {items.map(({ icon: Icon, title, description }, index) => (
        <div
          key={title}
          className={`flex items-start gap-3 px-3 py-5 sm:px-5 ${
            index % 2 !== 0 ? "border-l border-gray-200" : ""
          } ${index > 1 ? "border-t border-gray-200 sm:border-t-0" : ""} ${
            index > 0 ? "sm:border-l sm:border-gray-200" : ""
          }`}
        >
          <Icon className="h-5 w-5 shrink-0 text-[var(--color-primary)]" />
          <div>
            <h3 className="text-sm font-bold">{title}</h3>
            <p className="mt-1 text-xs leading-5 text-gray-500">{description}</p>
          </div>
        </div>
      ))}
    </section>
  );
}

function RealProductImages({ product, images }) {
  if (!images.length) return null;

  return (
    <section className="border-b border-gray-200 py-5" aria-labelledby="real-product-images-heading">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase text-[var(--color-primary)]">Giá xưởng · ảnh hoàn thiện</p>
          <h2 id="real-product-images-heading" className="mt-1 text-lg font-bold">Hình ảnh thực tế</h2>
        </div>
        <span className="shrink-0 rounded-full bg-[#f7f0ea] px-2.5 py-1 text-xs font-semibold text-[var(--color-primary)]">
          {images.length} ảnh
        </span>
      </div>
      <p className="mt-2 text-sm leading-6 text-gray-500">
        Ảnh sản phẩm đã hoàn thiện tại xưởng hoặc nhà khách. Màu sắc có thể thay đổi nhẹ theo ánh sáng và màn hình.
      </p>
      <div className="scrollbar-hide mt-3 flex snap-x gap-3 overflow-x-auto pb-1">
        {images.map((image, index) => (
          <a
            key={image}
            href={image}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAnalyticsEvent("view_real_product_image", {
              item_id: getProductId(product),
              item_name: product.name,
              image_index: index + 1,
            })}
            className="block h-24 w-32 shrink-0 snap-start overflow-hidden rounded-md bg-gray-100 ring-1 ring-gray-200 transition hover:ring-2 hover:ring-[var(--color-primary)] sm:h-28 sm:w-36"
            aria-label={`Xem ảnh thực tế ${index + 1} của ${product.name}`}
          >
            <img
              src={getOptimizedImageUrl(image, { width: 360 })}
              srcSet={getResponsiveImageSrcSet(image, [240, 360, 480])}
              sizes="144px"
              alt={`${product.name} thực tế ${index + 1}`}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </a>
        ))}
      </div>
    </section>
  );
}

function CustomerReviews({ product }) {
  const reviewSource = Array.isArray(product.reviews)
    ? product.reviews
    : Array.isArray(product.details?.reviews)
      ? product.details.reviews
      : [];
  const reviews = reviewSource.filter(
    (review) => review && (review.content || review.comment)
  );

  const average = reviews.length
    ? reviews.reduce((total, review) => total + Number(review.rating || 5), 0) / reviews.length
    : 0;

  return (
    <section className="border-t border-gray-200 py-12">
      <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
        <div>
          <p className="text-sm font-bold uppercase text-[var(--color-primary)]">Phản hồi</p>
          <h2 className="mt-2 text-2xl font-bold sm:text-3xl">Đánh giá khách hàng</h2>
          {reviews.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <div className="flex text-amber-500">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star
                    key={index}
                    className={`h-4 w-4 ${index < Math.round(average) ? "fill-current" : ""}`}
                  />
                ))}
              </div>
              <span className="text-sm font-semibold">{average.toFixed(1)} / 5</span>
              <span className="text-sm text-gray-500">({reviews.length} đánh giá đã xác minh)</span>
            </div>
          )}
        </div>
        <a
          href="https://www.facebook.com/cunghome21"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackContactClick("Facebook reviews", {
            content_ids: [getProductId(product)],
            content_name: product.name,
            content_type: "product",
          })}
          className="inline-flex min-h-11 items-center justify-center rounded-md bg-[var(--color-primary)] px-4 text-sm font-semibold !text-white transition hover:bg-[#805b42]"
        >
          Xem phản hồi trên fanpage
        </a>
      </div>

      {reviews.length > 0 ? (
        <div className="mt-7 grid gap-4 md:grid-cols-3">
          {reviews.slice(0, 3).map((review, index) => (
            <article key={review._id || index} className="rounded-lg border border-gray-200 bg-white p-5">
              <div className="flex text-amber-500">
                {Array.from({ length: 5 }).map((_, starIndex) => (
                  <Star
                    key={starIndex}
                    className={`h-4 w-4 ${starIndex < Number(review.rating || 5) ? "fill-current" : ""}`}
                  />
                ))}
              </div>
              <p className="mt-4 text-sm leading-6 text-gray-700">{review.content || review.comment}</p>
              <p className="mt-4 text-sm font-bold">{review.name || "Khách hàng Cưng Home"}</p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                <span>Đánh giá đã xác minh</span>
                {review.date && (
                  <time dateTime={review.date}>
                    {new Date(`${review.date}T00:00:00`).toLocaleDateString("vi-VN")}
                  </time>
                )}
                {review.sourceUrl && (
                  <a
                    href={review.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-semibold text-[var(--color-primary)] hover:underline"
                  >
                    Xem nguồn
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="mt-7 rounded-lg border border-gray-200 bg-white p-6 sm:flex sm:items-center sm:justify-between sm:gap-6">
          <div>
            <p className="font-semibold text-gray-800">Chưa có đánh giá đã xác minh cho sản phẩm này</p>
            <p className="mt-2 text-sm leading-6 text-gray-500">
              Cưng Home chỉ hiển thị nhận xét có nguồn rõ ràng. Bạn có thể xem phản hồi và hình ảnh khách hàng đã chia sẻ trên fanpage.
            </p>
          </div>
          <div className="mt-4 flex shrink-0 items-center gap-1 text-gray-300 sm:mt-0">
            {Array.from({ length: 5 }).map((_, index) => (
              <Star key={index} className="h-5 w-5" />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function PolicySection() {
  const policies = [
    {
      icon: ShieldCheck,
      title: "Chính sách bảo hành",
      summary: "Bảo hành 3 năm (36 tháng)",
      details: [
        "Áp dụng cho lỗi kỹ thuật thuộc kết cấu khung và phần sản xuất của Cưng Home.",
        "Thời hạn được tính từ ngày bàn giao sản phẩm.",
        "Không áp dụng cho hao mòn tự nhiên, sử dụng sai hướng dẫn hoặc tác động ngoại lực.",
      ],
    },
    {
      icon: FileCheck2,
      title: "Đặt hàng và xác nhận",
      summary: "Xác nhận cấu hình trước sản xuất",
      details: [
        "Kích thước, màu sắc, chất liệu và báo giá được xác nhận trước khi thực hiện.",
        "Sản phẩm may đo được sản xuất theo cấu hình khách hàng đã duyệt.",
        "Các thay đổi sau khi sản xuất bắt đầu có thể phát sinh chi phí.",
      ],
    },
    {
      icon: Truck,
      title: "Giao nhận và lắp đặt",
      summary: "Hẹn lịch theo khu vực",
      details: [
        "Thời gian giao được thông báo sau khi sản phẩm hoàn thiện.",
        "Khách hàng hỗ trợ cung cấp thông tin lối đi, thang máy hoặc cầu thang.",
        "Chi phí ngoài phạm vi hỗ trợ sẽ được báo trước khi xác nhận.",
      ],
    },
    {
      icon: RefreshCcw,
      title: "Điều chỉnh và đổi trả",
      summary: "Xử lý theo tình trạng thực tế",
      details: [
        "Sản phẩm lỗi do sản xuất được kiểm tra và đưa ra phương án khắc phục.",
        "Sản phẩm may đo không áp dụng đổi trả vì lý do thay đổi nhu cầu hoặc màu sắc.",
        "Vui lòng thông báo sớm kèm hình ảnh để được tiếp nhận nhanh chóng.",
      ],
    },
  ];

  return (
    <section className="border-t border-gray-200 py-12">
      <div className="max-w-3xl">
        <p className="text-sm font-bold uppercase text-[var(--color-primary)]">Thông tin mua hàng</p>
        <h2 className="mt-2 text-2xl font-bold sm:text-3xl">Chính sách Cưng Home</h2>
        <p className="mt-3 leading-7 text-gray-600">
          Nội dung dưới đây giúp khách hàng nắm rõ quy trình trước khi xác nhận sản xuất.
        </p>
      </div>

      <div className="mt-7 divide-y divide-gray-200 border-y border-gray-200">
        {policies.map((policy) => (
          <PolicyItem key={policy.title} {...policy} />
        ))}
      </div>
    </section>
  );
}

function PolicyItem({ icon: Icon, title, summary, details }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 !rounded-none !bg-transparent px-0 py-4 text-left !text-gray-800 !shadow-none"
      >
        <Icon className="h-5 w-5 shrink-0 text-[var(--color-primary)]" />
        <span className="min-w-0 flex-1">
          <span className="block font-bold">{title}</span>
          <span className="mt-1 block text-sm font-normal text-gray-500">{summary}</span>
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 transition ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <ul className="space-y-2 pb-5 pl-8 text-sm leading-6 text-gray-600">
              {details.map((detail) => (
                <li key={detail} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-1 h-3.5 w-3.5 shrink-0 text-[var(--color-primary)]" />
                  {detail}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function RelatedProducts({ currentProduct }) {
  const [others, setOthers] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE}/products`)
      .then((res) => res.json())
      .then((data) => {
        const localized = Array.isArray(data) ? data.map(localizeProduct) : [];
        const currentId = getProductId(currentProduct);
        const sameType = localized
          .filter((item) => getProductId(item) !== currentId && item.type === currentProduct.type)
          .sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a));
        const fallback = localized
          .filter((item) => getProductId(item) !== currentId && item.type !== currentProduct.type)
          .sort((a, b) => getProductTimestamp(b) - getProductTimestamp(a));

        setOthers([...sameType, ...fallback].slice(0, 4));
      })
      .catch((err) => console.error("Related products error:", err));
  }, [currentProduct]);

  if (!others.length) return null;

  return (
    <section className="border-t border-gray-200 pt-12">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase text-[var(--color-primary)]">Khám phá thêm</p>
          <h2 className="mt-2 text-2xl font-bold sm:text-3xl">Sản phẩm cùng nhóm</h2>
        </div>
        <Link
          to={`/?type=${encodeURIComponent(currentProduct.type || "Sofa")}#products`}
          className="hidden text-sm font-semibold text-[var(--color-primary)] hover:underline sm:block"
        >
          Xem tất cả
        </Link>
      </div>

      <div className="mt-7 grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
        {others.map((item) => (
          <ProductCard
            key={getProductId(item)}
            product={item}
            onClick={() =>
              trackProductSelect(item, { item_list_name: "related_products" })
            }
          />
        ))}
      </div>
    </section>
  );
}
